import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import {
	addHoldByID,
	getActiveInteraction,
	removeHoldByID,
	tryComplete,
} from '@atlaskit/react-ufo/interaction-metrics';
import UFORouteName from '@atlaskit/react-ufo/route-name-context';
import scheduleOnPaint from '@atlaskit/react-ufo/schedule-on-paint';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';

/**
 * Re-fire the page-load VC90 UFO metric as a `transition` interaction (initial=false) when a
 * user performs a subsequent search on an already-loaded search page (query resubmit or
 * filter change).
 *
 * The Confluence `ufo-search-page-ignoring-smart-answers` variant does NOT need to be
 * handled here: react-ufo derives it automatically. When any interaction whose `ufoName`
 * matches `config.extraSearchPageInteraction.searchPageMetricName` finishes, react-ufo's
 * `createExtraSearchPageInteractionPayload` clones it (excluding SAIN holds) and emits the
 * `-ignoring-smart-answers` twin — for transitions just as for page loads. Since our
 * transition reuses the primary `searchPageMetricName` (== `UFORouteName.current`), the
 * SAIN-ignoring transition twin is emitted for free.
 *
 * Why an imperative hold (rather than relying on the loading skeleton's hold):
 *
 * We want VC90 to measure from the moment the user starts the search (Enter / filter click)
 * until the new results are rendered — i.e. it must include URL sync + network + render. If
 * we instead started the transition when the loading skeleton mounts, we would miss the time
 * between the gesture and the skeleton commit (and, on a Relay cache hit, the skeleton may
 * not even suspend). So we start the transition synchronously at the gesture and immediately
 * take an imperative hold on the freshly-created interaction so it cannot settle during
 * navigation/network. The hold is released when the results finish rendering
 * (`releaseReSearchTransitionHold`, driven by `resultsState` reaching a terminal value),
 * which is exactly the VC90 completion point.
 */

// The interaction id currently held open by a re-search transition, or null when none is in
// flight. Module-level so the fire (input / filter gesture) and the release (main.tsx
// effect) can coordinate without extra React state.
let heldReSearchInteractionId: string | null = null;

const RE_SEARCH_TRANSITION_HOLD_NAME = 'search-page-re-search';
const RE_SEARCH_TRANSITION_HOLD_ID = 'search-page-re-search-hold';

/**
 * Start a re-search VC90 transition and hold it open until the results render.
 *
 * Fires `traceUFOTransition` for the current search page route and immediately takes an
 * imperative hold on the new interaction so it measures the full gesture→results duration.
 * No-op when the gate is off or no search page route is active.
 *
 * If a previous re-search transition is still in flight (e.g. the user searches again before
 * the first completes), its hold is released first so the old interaction can settle and the
 * new one starts cleanly.
 */
export function startReSearchTransition(): void {
	if (!fg('enable_same_page_re_search_vc90')) {
		return;
	}

	const ufoName = UFORouteName.current;
	if (!ufoName) {
		return;
	}

	// A rapid second search: release the previous hold so the first interaction can settle
	// before we abort it and start a new transition.
	if (heldReSearchInteractionId !== null) {
		releaseReSearchTransitionHold();
	}

	// Starts a new `transition` interaction and sets it as the active interaction.
	traceUFOTransition(ufoName);

	// Only hold a genuinely-new transition (traceUFOTransition may no-op on a sampling
	// coinflip miss, leaving the previous interaction active).
	const interaction = getActiveInteraction();
	if (!interaction || interaction.type !== 'transition') {
		return;
	}

	// Tag the interaction so this manually-fired re-search transition can be distinguished
	// from normal (router-driven) page-load / transition events in analytics. Surfaces on the
	// emitted UFO event under `customData` and is dropped when the interaction completes, so it
	// cannot leak into other VC90 events. Targets the interaction just created above.
	addUFOCustomData({ reSearch: true });

	addHoldByID(
		interaction.id,
		[{ name: RE_SEARCH_TRANSITION_HOLD_NAME }],
		RE_SEARCH_TRANSITION_HOLD_NAME,
		RE_SEARCH_TRANSITION_HOLD_ID,
	);
	heldReSearchInteractionId = interaction.id;
}

/**
 * Release the hold taken by `startReSearchTransition`, allowing the transition to complete at
 * the correct VC90 point. Called when the results reach a terminal state. No-op when no
 * re-search transition is in flight.
 *
 * Why the double `requestAnimationFrame` before `tryComplete`:
 *
 * The results terminal state (`resultsState` → RESULTS / NO_RESULTS / ERROR) is a React state
 * signal that fires inside a `useEffect` — i.e. *after* React has committed the new fragment
 * data to the tree, but *before* the browser has painted those DOM nodes. If we called
 * `tryComplete` synchronously here, it would stamp `interaction.end` at the pre-paint commit
 * moment, truncating VC90 to the commit time (observed as e.g. ~235ms) even though the real
 * visually-complete paint — which TTI/FMP and the VC observer measure — lands much later
 * (e.g. ~1742ms). That produced the "VC90 far lower than TTI/FMP" bug on fast re-searches.
 *
 * To finalise at the true paint boundary we defer completion with react-ufo's shared
 * `scheduleOnPaint` — the same primitive react-ufo's own segment `complete()` uses internally
 * (see `react-ufo/atlaskit/src/segment/segment.tsx`) — so the callback runs after the results
 * have actually painted, then remove our hold and complete. This lets the per-interaction VC
 * observer record the final (results) revision before the interaction settles, bringing VC90
 * back in line with TTI/FMP. `scheduleOnPaint` also covers the hidden-tab case (browsers
 * pause/throttle `requestAnimationFrame` in background tabs, so it falls back to
 * `setTimeout(cb, 100)` instead of leaving `finalise` dangling until the tab is foregrounded)
 * and SSR, so there is nothing else to special-case here.
 */
export function releaseReSearchTransitionHold(): void {
	if (heldReSearchInteractionId === null) {
		return;
	}

	const interactionId = heldReSearchInteractionId;
	heldReSearchInteractionId = null;

	scheduleOnPaint(() => {
		removeHoldByID(interactionId, RE_SEARCH_TRANSITION_HOLD_ID);
		// With our hold gone the interaction can complete once its other holds (results
		// segment / loading skeleton) have cleared.
		tryComplete(interactionId);
	});
}
